// Cache para evitar logs repetitivos
let apiUrlLogged = false;

/**
 * Utilitário para obter a URL base da API
 * 
 * SEMPRE usa VITE_API_URL se definida, independente do ambiente
 * Se não estiver definida, retorna string vazia para usar URLs relativas (proxy do Vite em dev)
 */
export function getApiUrl(): string {
  const apiUrl = import.meta.env.VITE_API_URL;
  
  // Se VITE_API_URL estiver definida e não for apenas "/api", usar ela
  if (apiUrl && apiUrl !== '/api' && apiUrl.trim() !== '') {
    // Remove barra final se existir
    const cleanedUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
    // Log apenas uma vez em desenvolvimento
    if (import.meta.env.DEV && !apiUrlLogged) {
      console.log('🔧 getApiUrl - Usando URL absoluta:', cleanedUrl);
      apiUrlLogged = true;
    }
    return cleanedUrl;
  }
  
  // Se não tiver URL definida, retorna string vazia (para dev com proxy do Vite)
  // Logar apenas uma vez em desenvolvimento para não poluir o console
  if (import.meta.env.DEV && !apiUrlLogged) {
    console.log('🔧 getApiUrl - VITE_API_URL não definida, usando proxy do Vite (normal em dev)');
    apiUrlLogged = true;
  }
  return '';
}

// Cache para evitar logs repetitivos de getApiPath
let apiPathLogged = false;

/**
 * Converte uma URL relativa da API para URL absoluta se necessário
 * 
 * @param path - Caminho relativo (ex: "/api/leads" ou "/api/tarefas")
 * @returns URL completa ou relativa dependendo do ambiente
 */
export function getApiPath(path: string): string {
  const apiUrl = getApiUrl();
  
  // Se não tiver URL base definida
  if (!apiUrl) {
    if (import.meta.env.DEV) {
      // Em desenvolvimento, usar localhost:3001 diretamente (sem proxy)
      const devApiUrl = 'http://localhost:3001';
      const normalizedPath = path.startsWith('/') ? path : `/${path}`;
      const fullUrl = normalizedPath.startsWith('/api') 
        ? `${devApiUrl}${normalizedPath}`
        : `${devApiUrl}/api${normalizedPath}`;
      
      // Log apenas uma vez para não poluir o console
      if (!apiPathLogged) {
        console.log('🔧 getApiPath - Em dev sem VITE_API_URL, usando:', devApiUrl);
        apiPathLogged = true;
      }
      return fullUrl;
    }
    
    // Em produção sem VITE_API_URL, tentar inferir do domínio atual
    // Isso funciona se frontend e backend estão no mesmo domínio (ex: Railway)
    if (typeof window !== 'undefined') {
      const currentOrigin = window.location.origin;
      const normalizedPath = path.startsWith('/') ? path : `/${path}`;
      const fullUrl = normalizedPath.startsWith('/api') 
        ? `${currentOrigin}${normalizedPath}`
        : `${currentOrigin}/api${normalizedPath}`;
      
      if (!apiPathLogged) {
        console.warn('⚠️ getApiPath - VITE_API_URL não definida em produção, usando origin atual:', currentOrigin);
        console.warn('⚠️ Configure VITE_API_URL para melhor controle da URL da API');
        apiPathLogged = true;
      }
      return fullUrl;
    }
    
    // Fallback: retornar path relativo (não ideal, mas melhor que quebrar)
    console.error('❌ getApiPath - Não foi possível determinar URL da API. Configure VITE_API_URL.');
    return path;
  }
  
  // Garante que o path começa com /
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  
  // Se o path já começar com /api, concatena diretamente
  if (normalizedPath.startsWith('/api')) {
    return `${apiUrl}${normalizedPath}`;
  }
  
  // Caso contrário, adiciona /api antes
  return `${apiUrl}/api${normalizedPath}`;
}

/**
 * Wrapper para fetch que automaticamente converte URLs relativas da API
 * Use esta função em vez de fetch() direto para chamadas à API
 * 
 * @param input - URL ou Request (pode ser relativa como "/api/leads")
 * @param init - Opções do fetch (RequestInit)
 * @returns Promise<Response>
 */
export function apiFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  let url = typeof input === 'string' ? input : (input as Request).url;
  
  // Se for uma URL relativa que começa com /api, converter para absoluta se necessário
  if (url.startsWith('/api')) {
    url = getApiPath(url);
  }
  
  // Se input era um Request, criar um novo com a URL atualizada
  if (typeof input !== 'string' && input instanceof Request) {
    return fetch(new Request(url, input), init);
  }
  
  return fetch(url, init);
}
